/**
 * Turning the editorial plan into something a timeline can draw.
 *
 * Kept out of the component so the date arithmetic — which is where this kind
 * of view usually goes wrong — can be reasoned about and tested on its own.
 */
import {
  COMPANY_LABELS,
  PILLAR_LABELS,
  type ContentItemDto,
  type ContentStatus,
} from '@nova/shared';
import { nextActivitySortKey } from './timeline-phases.js';

/**
 * 'next' is the default: a flat list ordered by whatever has to happen soonest.
 * Narrowing to one product area is what the filters in the top bar are for.
 */
export type Grouping = 'next' | 'area' | 'owner' | 'pillar';

export const DAY_MS = 86_400_000;

/** Midnight local time — bars are placed per day, not per millisecond. */
export function startOfDay(value: Date | string): Date {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function daysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);
}

export interface TimelineBar {
  item: ContentItemDto;
  /** Where work starts: the draft deadline, or creation when there is none. */
  start: Date;
  /** Planned publication, or the draft deadline when nothing is planned. */
  end: Date;
  /** Draft deadline drawn as a tick inside the bar. */
  deadline: Date | null;
  /** When it actually went out, from the channel confirmations. */
  publishedAt: Date | null;
  /**
   * Published later than planned. Kept for the tooltip only — the timeline no
   * longer shouts about it; the activity log is where lateness is chased.
   */
  wasLate: boolean;
}

export interface TimelineGroup {
  key: string;
  label: string;
  /** Sub-label, e.g. the company an area belongs to. */
  context?: string;
  color?: string;
  bars: TimelineBar[];
}

export interface TimelineModel {
  start: Date;
  end: Date;
  totalDays: number;
  groups: TimelineGroup[];
  /** Items with neither a deadline nor a publish date — nothing to place them on. */
  unplaced: ContentItemDto[];
}

/** An item can only be drawn if it has at least one date. */
export function isPlaceable(item: ContentItemDto): boolean {
  return Boolean(item.draftDeadline ?? item.publishAt);
}

function barFor(item: ContentItemDto): TimelineBar | null {
  if (!isPlaceable(item)) return null;

  const deadline = item.draftDeadline ? startOfDay(item.draftDeadline) : null;
  const planned = item.publishAt ? startOfDay(item.publishAt) : null;

  const confirmations = (item.channelTargets ?? [])
    .map((target) => target.publishedAt)
    .filter((value): value is string => Boolean(value))
    .map((value) => startOfDay(value).getTime());
  const publishedAt = confirmations.length > 0 ? new Date(Math.max(...confirmations)) : null;

  // Start at the deadline when there is one — that is when work must happen.
  // Otherwise fall back to creation, so the bar still has somewhere to begin.
  const start = deadline ?? startOfDay(item.createdAt);
  const end = planned ?? deadline ?? start;

  return {
    item,
    // A publish date before the deadline would otherwise draw backwards.
    start: start <= end ? start : end,
    end: start <= end ? end : start,
    deadline,
    publishedAt,
    wasLate:
      item.status === 'PUBLISHED' &&
      planned !== null &&
      publishedAt !== null &&
      publishedAt.getTime() > planned.getTime() + DAY_MS,
  };
}

/** Statuses that are over and done with — drawn muted rather than hidden. */
export const FINISHED_STATUSES: readonly ContentStatus[] = ['PUBLISHED', 'REJECTED', 'CANCELLED'];

export function buildTimeline(
  items: ContentItemDto[],
  options: { grouping: Grouping; lookbackDays: number; now?: Date },
): TimelineModel {
  const now = startOfDay(options.now ?? new Date());
  const bars = items.map(barFor).filter((bar): bar is TimelineBar => bar !== null);
  // Undated content is a decision queue; something already rejected or
  // cancelled is nobody's decision, so it stays out of it.
  const unplaced = items.filter(
    (item) => !isPlaceable(item) && !['REJECTED', 'CANCELLED'].includes(item.status),
  );

  // The window always covers the requested history and at least a fortnight
  // ahead, then stretches to fit whatever the plan actually contains.
  const windowStart = new Date(now.getTime() - options.lookbackDays * DAY_MS);
  const windowEnd = new Date(now.getTime() + 14 * DAY_MS);

  const visible = bars.filter((bar) => bar.end >= windowStart);
  const start = windowStart;
  const end = visible.reduce((latest, bar) => (bar.end > latest ? bar.end : latest), windowEnd);

  // Flat and ordered by urgency — one heading would only get in the way.
  if (options.grouping === 'next') {
    const bars = [...visible].sort(
      (a, b) => nextActivitySortKey(a.item, now) - nextActivitySortKey(b.item, now),
    );
    return {
      start,
      end,
      totalDays: Math.max(1, daysBetween(start, end) + 1),
      groups: bars.length > 0 ? [{ key: 'all', label: '', bars }] : [],
      unplaced: sortUnplaced(unplaced, now),
    };
  }

  const groups = new Map<string, TimelineGroup>();

  for (const bar of visible) {
    const { key, label, context, color } = groupOf(bar.item, options.grouping);
    const existing = groups.get(key);
    if (existing) {
      existing.bars.push(bar);
    } else {
      groups.set(key, { key, label, context, color, bars: [bar] });
    }
  }

  const ordered = [...groups.values()].sort((a, b) =>
    `${a.context ?? ''}${a.label}`.localeCompare(`${b.context ?? ''}${b.label}`, 'nb'),
  );
  // Within a group, still soonest-first — the question is always "what next".
  for (const group of ordered) {
    group.bars.sort((a, b) => nextActivitySortKey(a.item, now) - nextActivitySortKey(b.item, now));
  }

  return {
    start,
    end,
    totalDays: Math.max(1, daysBetween(start, end) + 1),
    groups: ordered,
    unplaced: sortUnplaced(unplaced, now),
  };
}

function sortUnplaced(items: ContentItemDto[], now: Date): ContentItemDto[] {
  return [...items].sort((a, b) => nextActivitySortKey(a, now) - nextActivitySortKey(b, now));
}

function groupOf(
  item: ContentItemDto,
  grouping: Grouping,
): { key: string; label: string; context?: string; color?: string } {
  if (grouping === 'owner') {
    return {
      key: item.ownerUserId ?? 'uten-eier',
      label: item.owner?.name ?? 'Uten ansvarlig',
    };
  }
  if (grouping === 'pillar') {
    return { key: item.pillar, label: PILLAR_LABELS[item.pillar] };
  }
  return {
    key: item.areaId,
    label: item.area?.name ?? 'Ukjent område',
    context: item.area ? COMPANY_LABELS[item.area.company] : undefined,
    color: item.area?.color,
  };
}

/** Month segments across the window, for the header and the annual-wheel strip. */
export function monthSegments(
  start: Date,
  end: Date,
): { year: number; month: number; offsetDays: number; days: number }[] {
  const segments: { year: number; month: number; offsetDays: number; days: number }[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);

  while (cursor <= end) {
    const monthStart = new Date(cursor);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const from = monthStart < start ? start : monthStart;
    const to = monthEnd > end ? end : monthEnd;

    segments.push({
      year: cursor.getFullYear(),
      month: cursor.getMonth() + 1,
      offsetDays: daysBetween(start, from),
      days: daysBetween(from, to) + 1,
    });

    cursor.setMonth(cursor.getMonth() + 1);
  }

  return segments;
}
